import { type ReactNode, useMemo, useState } from 'react';

import { Tabs } from 'cpool-components/general/Tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { TooltipVariant } from 'cpool-components/general/Tooltip/types';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { SearchInput } from 'cpool-components/input/SearchInput';
import { Empty } from 'cpool-components/state/Empty';
import { USER_ROLES } from 'cpool-constants';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { LuArchive, LuChevronLeft, LuChevronRight, LuListFilter, LuMessageCircle } from 'react-icons/lu';

import { ChatBlockedReason } from './ChatBlockedReason';
import { ChatListItem } from './ChatListItem';
import { ContactRows } from './ContactRows';
import Spinner from '~/components/icons/Spinner';
import { CHAT_LIST_VARIANTS, type ChatListVariant } from '~/features/whatsapp/constants/chatListVariants';
import { CHAT_LIST_FILTERS, type ChatListFilterId } from '~/features/whatsapp/constants/chatStatus';
import { useChats } from '~/features/whatsapp/hooks/useChats';
import { useInboxUnreadSummary } from '~/features/whatsapp/hooks/useInboxUnreadSummary';
import { useLeadChats } from '~/features/whatsapp/hooks/useLeadChats';
import { usePartnerChatTabs } from '~/features/whatsapp/hooks/usePartnerChatTabs';
import {
    CHAT_CONTACT_TABS,
    CHAT_LIST_MODES,
    CHAT_PANEL_VIEWS,
    type ChatContactTab,
    leaveLeadScope,
    setActiveChat,
    setContactTab,
    setListMode,
    useChatPanelCurrentView,
    useChatPanelStore,
} from '~/features/whatsapp/stores/chatPanelStore';
import { deriveChatDisplayName } from '~/features/whatsapp/utils/deriveChatDisplayName';
import { deriveChatStatusLabel } from '~/features/whatsapp/utils/deriveChatStatus';
import { getVisibilityHintKey } from '~/features/whatsapp/utils/getVisibilityHintKey';
import { isPartnerChat } from '~/features/whatsapp/utils/isPartnerChat';
import { unreadByChatIdFromSummary } from '~/features/whatsapp/utils/unreadByChatIdFromSummary';
import useUserInfo from '~/hooks/useUserInfo';
import { cn } from '~/utils/cnTailwind';

const FILTER_MENU_MAX_HEIGHT = 280;
const FILTER_MENU_MIN_WIDTH = 200;

const isChatContactTab = (id: string): id is ChatContactTab =>
    (Object.values(CHAT_CONTACT_TABS) as Array<string>).includes(id);

const BASE_FILTER_ORDER: Array<ChatListFilterId> = [
    CHAT_LIST_FILTERS.ALL,
    CHAT_LIST_FILTERS.NAME_AZ,
    CHAT_LIST_FILTERS.ANSWERED,
    CHAT_LIST_FILTERS.UNREAD,
    CHAT_LIST_FILTERS.AWAITING_CUSTOMER_REPLY,
    CHAT_LIST_FILTERS.WINDOW_OPEN,
    CHAT_LIST_FILTERS.WINDOW_EXPIRED,
    CHAT_LIST_FILTERS.NO_MESSAGES,
];

type Props = {
    variant?: ChatListVariant;
};

export const ChatsListView = ({ variant = CHAT_LIST_VARIANTS.PANEL }: Props) => {
    const { t } = useTranslation();
    const user = useUserInfo();
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<ChatListFilterId>(CHAT_LIST_FILTERS.ALL);

    const filterOrder = useMemo(() => {
        if (user?.role === USER_ROLES.division_leader || user?.role === USER_ROLES.team_leader) {
            return [...BASE_FILTER_ORDER, CHAT_LIST_FILTERS.UNLINKED];
        }
        return BASE_FILTER_ORDER;
    }, [user?.role]);
    const filterOptions = useMemo(
        () =>
            filterOrder.map((filterId) => ({
                label: t(`WHATSAPP_CHAT.LIST.FILTERS.${filterId.toUpperCase()}` as ParseKeys),
                value: filterId,
            })),
        [filterOrder, t],
    );

    // Both hooks always called — useLeadChats layers on useChats's cache, no extra network request.
    const leadDetailsScopeLoanRequestId = useChatPanelStore.use.leadDetailsScopeLoanRequestId();
    const pendingChatIds = useChatPanelStore.use.pendingChatIds();
    const activeChatId = useChatPanelStore.use.activeChatId();
    const listMode = useChatPanelStore.use.listMode();
    const view = useChatPanelCurrentView();
    const allChats = useChats();
    const leadChats = useLeadChats(leadDetailsScopeLoanRequestId ?? '');
    const { data: leadOrAllChats, isError, isLoading } = leadDetailsScopeLoanRequestId ? leadChats : allChats;
    // Lead scope lists every chat of the lead; the inbox splits active vs archived (resolved).
    const isArchiveView = !leadDetailsScopeLoanRequestId && listMode === CHAT_LIST_MODES.ARCHIVED;
    const scopedChats = useMemo(
        () =>
            leadDetailsScopeLoanRequestId
                ? leadOrAllChats
                : leadOrAllChats?.filter((chat) => chat.resolved === isArchiveView),
        [isArchiveView, leadDetailsScopeLoanRequestId, leadOrAllChats],
    );

    const {
        contactDisabledReason,
        contactRows,
        contactsDisabled,
        contactTab,
        handleContactClick,
        isPartnerContactsError,
        isPartnerTab,
        showContactTabs,
        showSections,
    } = usePartnerChatTabs({
        allChats: allChats.data,
        clearSearch: () => setSearch(''),
        isLeadScoped: Boolean(leadDetailsScopeLoanRequestId),
        search,
    });
    // Defensive dedupe, as in `rows` — Chats has no unique index on `id`. Active only, like `tabChatsCount`.
    const allChatsCount = useMemo(
        () => new Set((allChats.data ?? []).filter((chat) => !chat.resolved).map((chat) => chat.id)).size,
        [allChats.data],
    );

    // A chat linked to both a lead and a partner shows up in both tabs.
    const chats = useMemo(() => {
        if (!scopedChats || !showContactTabs) return scopedChats;
        return scopedChats.filter((chat) =>
            isPartnerTab ? isPartnerChat(chat) : !isPartnerChat(chat) || chat.loanRequestIds.length > 0,
        );
    }, [isPartnerTab, scopedChats, showContactTabs]);

    // Two-KN open path: chatId immediate; spinner until refetch lands.
    const hasPendingChats = useMemo(() => {
        if (pendingChatIds.length === 0) return false;
        if (!chats) return true;
        const loadedChatIds = new Set(chats.map((c) => c.id));
        return pendingChatIds.some((id) => !loadedChatIds.has(id));
    }, [chats, pendingChatIds]);
    const { data: unreadSummary } = useInboxUnreadSummary();
    const unreadByChatId = useMemo(() => unreadByChatIdFromSummary(unreadSummary), [unreadSummary]);

    const rows = useMemo(() => {
        if (!chats) return [];

        // Defensive dedupe — Chats has no unique index on `id`; a pre-fix Mongo row may duplicate.
        const uniqueChats = [...new Map(chats.map((c) => [c.id, c])).values()];

        // Archive view hides search/filter, so neither applies there.
        const searchLower = isArchiveView ? '' : search.trim().toLowerCase();
        const activeFilter = isArchiveView ? CHAT_LIST_FILTERS.ALL : filter;
        let filtered = uniqueChats.filter((chat) => {
            const name = deriveChatDisplayName(chat).toLowerCase();
            return !searchLower || name.includes(searchLower);
        });

        if (activeFilter === CHAT_LIST_FILTERS.UNREAD) {
            filtered = filtered.filter((chat) => (unreadByChatId.get(chat.id) ?? 0) > 0);
        } else if (activeFilter === CHAT_LIST_FILTERS.UNLINKED) {
            filtered = filtered.filter((chat) => chat.loanRequestIds.length === 0 && !isPartnerChat(chat));
        } else if (activeFilter !== CHAT_LIST_FILTERS.ALL && activeFilter !== CHAT_LIST_FILTERS.NAME_AZ) {
            filtered = filtered.filter((chat) => deriveChatStatusLabel(chat) === activeFilter);
        }

        const sorted = [...filtered];
        if (activeFilter === CHAT_LIST_FILTERS.NAME_AZ) {
            sorted.sort((a, b) => deriveChatDisplayName(a).localeCompare(deriveChatDisplayName(b)));
        } else {
            sorted.sort((a, b) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0));
        }
        return sorted;
    }, [chats, filter, isArchiveView, search, unreadByChatId]);

    const isPage = variant === CHAT_LIST_VARIANTS.PAGE;
    // Lead scope never reaches the page (closePanel on mount) — fall back to the panel header if it does.
    const showPageHeader = isPage && !leadDetailsScopeLoanRequestId;
    // activeChatId lingers after popping back to the list view — highlight only while a chat/user info is open.
    const openChatId = isPage && view !== CHAT_PANEL_VIEWS.LIST ? activeChatId : null;
    const tabChatsCount = useMemo(() => new Set((chats ?? []).map((chat) => chat.id)).size, [chats]);

    let emptyStateContent: ReactNode;
    if (hasPendingChats) {
        emptyStateContent = <Spinner />;
    } else if (leadDetailsScopeLoanRequestId) {
        emptyStateContent = <ChatBlockedReason loanRequestId={leadDetailsScopeLoanRequestId} />;
    } else if (isArchiveView) {
        emptyStateContent = <Empty title={t('WHATSAPP_CHAT.LIST.ARCHIVED_EMPTY')} />;
    } else {
        emptyStateContent = <Empty title={t('WHATSAPP_CHAT.LIST.EMPTY')} />;
    }

    const openArchive = () => {
        setFilter(CHAT_LIST_FILTERS.ALL);
        setSearch('');
        setListMode(CHAT_LIST_MODES.ARCHIVED);
    };

    const archiveHeader = (
        <div className="mb-3 flex items-center gap-2">
            <button
                type="button"
                onClick={() => setListMode(CHAT_LIST_MODES.ACTIVE)}
                aria-label={t('BUTTONS.BACK')}
                className="flex items-center text-midnight"
            >
                <LuChevronLeft className="size-5" />
            </button>
            <span className="text-lg font-semibold text-midnight">{t('WHATSAPP_CHAT.LIST.ARCHIVED_TITLE')}</span>
        </div>
    );

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className={cn('shrink-0', isPage ? 'px-4 pt-4' : 'px-6 pt-6')}>
                {showPageHeader && isArchiveView && archiveHeader}
                {showPageHeader && !isArchiveView && (
                    <div className="mb-3 flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-4">
                            <span className="text-lg font-semibold text-midnight">{t('WHATSAPP_CHAT.LIST.TITLE')}</span>
                            <span className="truncate rounded-full bg-grey-table-hover px-2 py-0.5 text-xs text-grey-note">
                                {t(getVisibilityHintKey(user?.role, isPartnerTab))}
                            </span>
                        </div>
                        <span className="shrink-0 text-sm text-grey-note">
                            {t('WHATSAPP_CHAT.LIST.OVERVIEW_COUNT', { count: tabChatsCount })}
                        </span>
                    </div>
                )}
                {!showPageHeader && (
                    <>
                        <div className="mb-4 flex items-center gap-2">
                            <LuMessageCircle className="text-midnight" />
                            <span className="font-bold text-midnight">{t('WHATSAPP_CHAT.HEADER_BUTTON.LABEL')}</span>
                            {!leadDetailsScopeLoanRequestId && (
                                <span className="rounded-full bg-grey-light px-2 py-0.5 text-xs text-grey">
                                    {t(getVisibilityHintKey(user?.role, isPartnerTab))}
                                </span>
                            )}
                        </div>
                        {isArchiveView ? (
                            archiveHeader
                        ) : (
                            <div className="mb-2 flex items-center justify-between gap-2">
                                <span className="text-base font-bold text-midnight">
                                    {leadDetailsScopeLoanRequestId
                                        ? t('WHATSAPP_CHAT.LIST.LEAD_SCOPED_TITLE')
                                        : t('WHATSAPP_CHAT.LIST.TITLE')}
                                </span>
                                {leadDetailsScopeLoanRequestId && (
                                    <button
                                        type="button"
                                        onClick={leaveLeadScope}
                                        className="flex items-center gap-1 text-sm text-grey hover:text-midnight"
                                    >
                                        {t('WHATSAPP_CHAT.LIST.ALL_CHATS_LINK', { count: allChatsCount })}
                                        <LuChevronRight className="size-4" />
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                )}
                {showContactTabs && (
                    <Tabs
                        className="mb-3"
                        listClassName="w-full"
                        tabs={Object.values(CHAT_CONTACT_TABS).map((tab) => ({
                            id: tab,
                            label: t(`WHATSAPP_CHAT.LIST.TABS.${tab}`),
                        }))}
                        selectedId={contactTab}
                        onSelect={(id) => {
                            if (isChatContactTab(id)) setContactTab(id);
                        }}
                    />
                )}
                {isArchiveView && <p className="mb-3 text-xs text-grey">{t('WHATSAPP_CHAT.LIST.ARCHIVED_HINT')}</p>}
                {/* Search/filters hidden in lead-scoped and archive views — at most a handful of rows. */}
                {!leadDetailsScopeLoanRequestId && !isArchiveView && (
                    <div className="mb-3 flex items-center gap-3">
                        <DropdownMenu
                            options={filterOptions}
                            maxHeight={FILTER_MENU_MAX_HEIGHT}
                            minPanelWidth={FILTER_MENU_MIN_WIDTH}
                            value={filter}
                            onChange={(value) => {
                                if (!Array.isArray(value)) setFilter(value);
                            }}
                            renderTrigger={() => (
                                <button
                                    type="button"
                                    aria-label={t('WHATSAPP_CHAT.LIST.FILTER_BUTTON_LABEL')}
                                    className={cn(
                                        'flex size-8 items-center justify-center rounded text-midnight hover:bg-grey-hover',
                                        filter !== CHAT_LIST_FILTERS.ALL && 'bg-midnight text-white hover:bg-midnight',
                                    )}
                                >
                                    <LuListFilter className="size-5" />
                                </button>
                            )}
                        />
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={openArchive}
                                    aria-label={t('WHATSAPP_CHAT.LIST.ARCHIVE_BUTTON_LABEL')}
                                    className="flex size-8 items-center justify-center rounded text-midnight hover:bg-grey-hover"
                                >
                                    <LuArchive className="size-5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent hideArrow side="bottom" variant={TooltipVariant.LIGHT}>
                                {t('WHATSAPP_CHAT.LIST.ARCHIVE_BUTTON_LABEL')}
                            </TooltipContent>
                        </Tooltip>
                        <SearchInput
                            className="flex-1"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClear={() => setSearch('')}
                            placeholder={t('WHATSAPP_CHAT.LIST.SEARCH_PLACEHOLDER')}
                        />
                    </div>
                )}
            </div>

            <div className={cn('min-h-0 flex-1 overflow-y-auto', isPage && 'flex flex-col gap-1.5 pb-4')}>
                {isLoading && (
                    <div className="flex items-center justify-center py-10">
                        <Spinner />
                    </div>
                )}
                {isError && (
                    <div className="px-6 py-4 text-sm text-alert-error">
                        {t('WHATSAPP_CHAT.ERROR.LOAD_CHATS_FAILED')}
                    </div>
                )}
                {isPartnerTab && isPartnerContactsError && (
                    <div className="px-6 py-4 text-sm text-alert-error">
                        {t('WHATSAPP_CHAT.ERROR.CONTACTS_LOAD_ERROR')}
                    </div>
                )}
                {!isLoading &&
                    !isError &&
                    !(isPartnerTab && isPartnerContactsError) &&
                    rows.length === 0 &&
                    contactRows.length === 0 && (
                        <div className="flex items-center justify-center py-10">{emptyStateContent}</div>
                    )}
                {showSections && rows.length > 0 && (
                    <div className="px-4 pb-1 pt-3 text-sm font-semibold text-midnight">
                        {t('WHATSAPP_CHAT.LIST.TITLE')}
                    </div>
                )}
                {rows.map((chat) => (
                    <ChatListItem
                        key={chat.id}
                        chat={chat}
                        isActive={chat.id === openChatId}
                        variant={variant}
                        unreadCount={unreadByChatId.get(chat.id) ?? 0}
                        onClick={() => setActiveChat(chat.id)}
                    />
                ))}
                {showSections && contactRows.length > 0 && (
                    <ContactRows
                        contacts={contactRows}
                        disabled={contactsDisabled}
                        disabledReason={contactDisabledReason}
                        onContactClick={handleContactClick}
                    />
                )}
            </div>

            {!isPage && (
                <div className="shrink-0 border-t border-outline-light px-6 py-3 text-center text-xs text-grey">
                    {t('WHATSAPP_CHAT.LIST.FOOTER')}
                </div>
            )}
        </div>
    );
};
